import type { JsonSchemaObject } from '@onefootprint/request-types/dashboard';

// A JSON schema node as it actually appears at runtime. `JsonSchemaObject` types
// `properties` values as `unknown`, so we narrow to the bits we care about here.
type SchemaNode = {
  type?: JsonSchemaObject['type'];
  enum?: Array<unknown>;
  properties?: Record<string, unknown>;
  items?: unknown;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Reconcile the value at a single schema node against whatever the user has
 * already entered. Existing values are preserved when they're still valid for
 * the schema; otherwise we fall back to a basic placeholder ("foo" for strings,
 * 0 for numbers, false for booleans, the first enum value, etc.).
 *
 * "Still valid" matters most for enums: if the user picked a value that has
 * since been removed from the enum, keeping it would produce mock data that no
 * longer satisfies the schema, so we reset it to the first allowed value.
 */
const reconcile = (node: unknown, existing: unknown): unknown => {
  if (!isPlainObject(node)) {
    return existing === undefined ? 'foo' : existing;
  }
  const schema = node as SchemaNode;

  if (Array.isArray(schema.enum) && schema.enum.length > 0) {
    return schema.enum.includes(existing) ? existing : schema.enum[0];
  }

  switch (schema.type) {
    case 'integer':
    case 'number':
      return typeof existing === 'number' ? existing : 0;
    case 'boolean':
      return typeof existing === 'boolean' ? existing : false;
    case 'null':
      return null;
    case 'string':
      return typeof existing === 'string' ? existing : 'foo';
    case 'object':
      return reconcileObject(schema.properties, existing);
    case 'array':
      if (Array.isArray(existing)) {
        return schema.items === undefined ? existing : existing.map(item => reconcile(schema.items, item));
      }
      return schema.items === undefined ? [] : [reconcile(schema.items, undefined)];
    default:
      return existing === undefined ? 'foo' : existing;
  }
};

const reconcileObject = (properties: unknown, existing: unknown): Record<string, unknown> => {
  const props = isPlainObject(properties) ? properties : {};
  const existingObj = isPlainObject(existing) ? existing : {};

  // Only emit keys described by the schema. Keys the user had for fields that
  // were since removed from the schema are dropped, keeping the mock data in
  // sync with the schema rather than accumulating stale fields.
  const result: Record<string, unknown> = {};
  for (const [key, child] of Object.entries(props)) {
    result[key] = reconcile(child, existingObj[key]);
  }
  return result;
};

/**
 * Given an extraction schema and the current (stringified) mock data, return a
 * pretty-printed JSON string with placeholder values filled in for every schema
 * property. Values the user has already set are preserved as long as they're
 * still valid for the schema, so this is safe to run on every save.
 */
const generateMockData = (schema: JsonSchemaObject, currentMockData?: string): string => {
  let existing: unknown = {};
  if (currentMockData) {
    try {
      existing = JSON.parse(currentMockData);
    } catch {
      existing = {};
    }
  }

  const reconciled = reconcileObject(schema.properties, existing);
  return JSON.stringify(reconciled, null, 2);
};

export default generateMockData;
